import { useEffect, useState } from 'react';
import { api } from '../utils/api';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { HelpCircle, ChevronDown } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function FAQ() {
  const [faqs, setFaqs] = useState([]);
  const [openIds, setOpenIds] = useState(new Set());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/faqs');
        setFaqs(data);
      } catch {}
      finally { setLoading(false); }
    })();
  }, []);

  const toggle = (id) => {
    const next = new Set(openIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    setOpenIds(next);
  };

  return (
    <div style={{ backgroundColor: '#FDFBF7', minHeight: '100vh' }}>
      <Header />

      <section className="relative overflow-hidden">
        <div className="absolute inset-0" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?crop=entropy&cs=srgb&fm=jpg&w=1920&q=85)', backgroundSize: 'cover', backgroundPosition: 'center', filter: 'saturate(0.9) brightness(1.05)' }} aria-hidden />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(120deg, rgba(253, 251, 247, 0.94) 0%, rgba(243, 239, 231, 0.9) 50%, rgba(253, 251, 247, 0.85) 100%)' }} aria-hidden />
        <div className="max-w-4xl mx-auto px-6 py-20 text-center relative">
          <div className="inline-flex items-center gap-3 mb-4">
            <HelpCircle className="w-8 h-8" style={{ color: '#E57A44', strokeWidth: 1.5 }} />
            <h1 className="text-4xl sm:text-5xl font-semibold" style={{ fontFamily: 'Playfair Display, serif', color: '#2C2724' }} data-testid="faq-title">
              Frequently Asked
            </h1>
          </div>
          <p className="text-lg" style={{ fontFamily: 'Manrope, sans-serif', color: '#5C564D' }}>
            Straight answers to the most common questions about this space and how it works.
          </p>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-6 py-16">
        {loading ? (
          <p className="text-center py-12" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>Loading…</p>
        ) : faqs.length === 0 ? (
          <div className="text-center py-16 rounded-3xl" style={{ backgroundColor: '#FFFFFF', boxShadow: '0 8px 30px rgba(0,0,0,0.04)' }} data-testid="faq-empty">
            <HelpCircle className="w-12 h-12 mx-auto mb-4" style={{ color: '#E57A44' }} />
            <p className="mb-4" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>No FAQs yet — but you're welcome to reach out with any question.</p>
            <Link to="/contact" className="inline-block px-6 py-2 rounded-full" style={{ fontFamily: 'Nunito, sans-serif', backgroundColor: '#E57A44', color: '#FDFBF7', fontWeight: 600 }}>Send a Message</Link>
          </div>
        ) : (
          <div className="space-y-3" data-testid="faq-list">
            {faqs.map((f) => {
              const open = openIds.has(f.id);
              return (
                <div key={f.id} className="rounded-2xl overflow-hidden" style={{ backgroundColor: '#FFFFFF', boxShadow: '0 4px 20px rgba(0,0,0,0.04)' }} data-testid={`faq-item-${f.id}`}>
                  <button onClick={() => toggle(f.id)} className="w-full px-6 py-5 flex items-center justify-between gap-4 text-left transition-colors duration-200 hover:bg-[#FDFBF7]" data-testid={`faq-toggle-${f.id}`}>
                    <span className="font-semibold" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>{f.question}</span>
                    <ChevronDown className="w-5 h-5 shrink-0 transition-transform" style={{ color: '#E57A44', transform: open ? 'rotate(180deg)' : 'none' }} />
                  </button>
                  {open && (
                    <div className="px-6 pb-6" style={{ fontFamily: 'Lora, serif', color: '#2C2A29', lineHeight: 1.8, whiteSpace: 'pre-wrap' }} data-testid={`faq-answer-${f.id}`}>
                      {f.answer}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        <div className="text-center mt-12 p-8 rounded-3xl" style={{ backgroundColor: '#F3EFE7' }}>
          <p className="mb-4" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>Still have questions?</p>
          <Link to="/contact" className="inline-block px-6 py-3 rounded-full transition-all duration-300 hover:-translate-y-1" style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#E57A44', color: '#FDFBF7', fontWeight: 600 }} data-testid="faq-contact-cta">Reach Out</Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
